import { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import throttle from 'transmute/throttle';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'cust... Remove this comment to see the full error message

import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import Raven from 'raven-js';
import ErrorBoundary from 'crm-activity-creator-widgets/buttons/ErrorBoundary';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { BUTTON_COMPONENTS } from '../constants/ButtonComponents';
import OverflowActivityButton from './OverflowActivityButton';
import CreateActivityButtonsError from './CreateActivityButtonsError';
import ErrorButton from './ErrorButton';
import CreateActivityButtonsSkeleton from './CreateActivityButtonsSkeleton';
import { LINKED_IN } from 'crm-activity-creator-data/interaction/InteractionTypes';
import { DEFAULT_MAX_COUNT, LARGE_BUTTON_WIDTH, MEDIUM_BUTTON_WIDTH, BUTTON_MARGIN, SCROLLBAR_WIDTH } from '../constants/ButtonConstants';
import userInfoPromise from 'hub-http/userInfo';
import { AuthProvider } from 'crm-activity-creator-data/auth/AuthContext';
import { CRM_ERROR_BOUNDARY } from 'customer-data-tracking/constants/eventNames';
import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { CreateActivityPermissionsContext } from 'crm-activity-creator-data/permission/CreateActivityPermissionsContext';
import { useCurrentUserCanCommunicate } from 'crm-activity-creator-data/permission/hooks/useCurrentUserCanCommunicate';
import { LG, MD, SM } from 'crm-activity-creator-data/types/ActivityButtonProps';
import { Metrics } from 'crm-activity-creator-data/utils/Metrics';
import { BUTTON_ERRORS } from 'crm-activity-creator-data/constants/errors';
import { gate } from 'hub-http/gates';
import { OverrideCreateButtonContext } from 'crm-activity-creator-data/context/OverrideCreateButtonContext';
import SkeletonButton from './skeleton/SkeletonButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CreateActivityButtons({
  allActivityButtons,
  allAssociationTypesFromObjectTypeToEngagements,
  enableOverflow = false,
  enableReorder = false,
  isError,
  isLoading,
  maxVisible = DEFAULT_MAX_COUNT,
  objectTypeId,
  onUpdate,
  RhumbMarkerSuccess = null,
  subjectId: objectId,
  taskId,
  useCallingWidget = false,
  userInfo,
  userPermissions,
  OverrideCreateButtonComponent,
  paddingBlockStart
}) {
  const [auth, setAuth] = useState(userInfo);
  const [hasAuthError, setHasAuthError] = useState(false);
  const [buttonModal, setButtonModal] = useState();
  const listRef = useRef(undefined);
  const [buttonSize, setButtonSize] = useState(LG);
  const isUngatedToAppObjects = auth && auth.gates.includes(gate('CRM:ActivityCreator:AppObjects'));
  allActivityButtons = allActivityButtons.filter(button => {
    switch (button) {
      case LINKED_IN:
        return objectTypeId === CONTACT_TYPE_ID || objectTypeId === COMPANY_TYPE_ID;
      default:
        return true;
    }
  });
  useEffect(() => {
    if (!auth) {
      userInfoPromise().then(info => {
        setAuth(info);
      }).catch(() => setHasAuthError(true));
    }
  }, [auth]);
  const {
    permissionsLoading,
    permissionsError,
    permissionsData
  } = useCurrentUserCanCommunicate({
    objectId: Number(objectId),
    objectType: denormalizeTypeId(objectTypeId),
    userPermissions
  });
  const permissions = useMemo(() => {
    if (!permissionsData || !permissionsData.crmObject || permissionsLoading) {
      return {};
    }
    return permissionsData.crmObject.userPermissions;
  }, [permissionsData, permissionsLoading]);

  // Always show at least one button.
  // The visible buttons include the overflow, so account for it if enabled.
  const maxVisibleNonOverflow = Math.max(maxVisible, 1) - (enableOverflow ? 1 : 0);
  const selectedActivityButtons = allActivityButtons.slice(0, maxVisibleNonOverflow);
  const largeButtonTotalWidth = maxVisible * LARGE_BUTTON_WIDTH;
  const mediumButtonTotalWidth = maxVisible * MEDIUM_BUTTON_WIDTH;
  const totalButtonMargin = maxVisible * BUTTON_MARGIN;
  const lastButtonSize = useRef(null);
  const lastContentRecWidth = useRef(null);
  const onResizeThrottled = useMemo(() => throttle(200, entry => {
    const contentRecWidth = entry.contentRect.width;
    let resizeButtonSize = LG;
    if (contentRecWidth > largeButtonTotalWidth + totalButtonMargin) {
      resizeButtonSize = LG;
    } else if (contentRecWidth > mediumButtonTotalWidth + totalButtonMargin) {
      resizeButtonSize = MD;
    } else {
      resizeButtonSize = SM;
    }

    // This is needed because when the buttons switch between sizes, it may cause the scrollbar to appear or disappear and the
    // content width to change which causes the buttons to switch back and forth between sizes. So we need to check to make sure
    // that there is more than a scrollbars width difference between the last time the buttons changed size
    if (resizeButtonSize !== lastButtonSize.current && Math.abs(lastContentRecWidth.current || 0 - contentRecWidth) > SCROLLBAR_WIDTH) {
      setButtonSize(resizeButtonSize);
      lastButtonSize.current = resizeButtonSize;
      lastContentRecWidth.current = contentRecWidth;
    }
  }), [largeButtonTotalWidth, mediumButtonTotalWidth, setButtonSize, totalButtonMargin]);
  const observer = useMemo(() => new ResizeObserver(([entry]) => {
    onResizeThrottled(entry);
  }), [onResizeThrottled]);
  const sizeRef = useCallback(node => {
    if (node !== null) {
      listRef.current = node;
      observer.observe(node);
    } else if (listRef.current !== undefined) {
      observer.unobserve(listRef.current);
      listRef.current = undefined;
    }
  }, [observer]);
  const handleOpenModal = useCallback(modal => {
    setButtonModal(modal);
  }, []);
  const handleCloseModal = useCallback(() => {
    setButtonModal(null);
  }, []);
  const renderedSelectedButtons = useMemo(() => {
    if (!selectedActivityButtons) {
      return null;
    }
    return selectedActivityButtons.map(interactionName => {
      const ButtonComponent = BUTTON_COMPONENTS[interactionName];
      const handleOnButtonDidCatch = error => {
        Metrics.counter(BUTTON_ERRORS, {
          interaction_type: interactionName
        }).increment();
        Raven.captureMessage('Error encountered in activity creator button', {
          fingerprint: ['ERROR_ACTIVITY_CREATOR_BUTTON'],
          extra: {
            objectTypeId,
            subjectId: objectId,
            interactionName
          }
        });
        CrmLogger.log(CRM_ERROR_BOUNDARY, {
          action: error && (error.message || 'Unknown error'),
          type: 'activity-creator-single-button-boundary'
        });
      };
      const renderedErroredButton = () => /*#__PURE__*/_jsx(ErrorButton, {
        buttonSize: buttonSize,
        interactionName: interactionName || '',
        objectId: objectId,
        objectTypeId: objectTypeId
      });
      return /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: `activity-button-boundary-${interactionName}`,
        onComponentDidCatch: handleOnButtonDidCatch,
        ErrorComponent: renderedErroredButton,
        children: /*#__PURE__*/_jsx(ButtonComponent, {
          interactionName: interactionName,
          objectId: objectId,
          objectTypeId: objectTypeId,
          taskId: taskId,
          buttonSize: buttonSize,
          useCallingWidget: useCallingWidget,
          onOpenModal: handleOpenModal,
          onCloseModal: handleCloseModal,
          onClickActionComplete: emptyFunction
        })
      }, interactionName);
    });
  }, [selectedActivityButtons, objectId, objectTypeId, taskId, buttonSize, useCallingWidget, handleOpenModal, handleCloseModal]);
  if (isError || permissionsError || hasAuthError) {
    return /*#__PURE__*/_jsx(CreateActivityButtonsError, {});
  }
  if (isLoading || permissionsLoading || !auth) {
    // This will be removed by the end of Q4 2025 by @kazim. We need to get this in for inbound so putting a hacky fix in for now.
    if (OverrideCreateButtonComponent) {
      return /*#__PURE__*/_jsx(SkeletonButton, {
        width: 140,
        height: 32
      });
    }
    return /*#__PURE__*/_jsx("div", {
      ref: sizeRef,
      children: /*#__PURE__*/_jsx(CreateActivityButtonsSkeleton, {
        buttonSize: buttonSize
      })
    });
  }

  // We should only render the activity creator buttons if there are association types for the object type since things like App Objects may
  // not have any association types. Passing in null for allAssociationTypesFromObjectTypeToEngagements should bypass this check.
  if (isUngatedToAppObjects && allAssociationTypesFromObjectTypeToEngagements && allAssociationTypesFromObjectTypeToEngagements.length === 0) {
    return null;
  }
  const renderOverflowActivityButton = enableOverflow ? /*#__PURE__*/_jsx(OverflowActivityButton, {
    allActivityButtons: allActivityButtons,
    objectId: objectId,
    objectTypeId: objectTypeId,
    taskId: taskId,
    buttonSize: buttonSize,
    maxVisible: maxVisibleNonOverflow,
    enableReorder: enableReorder,
    isError: isError || permissionsError,
    useCallingWidget: useCallingWidget,
    onOpenModal: handleOpenModal,
    onCloseModal: handleCloseModal,
    onUpdate: onUpdate
  }) : null;
  return /*#__PURE__*/_jsx(AuthProvider, {
    gates: auth.gates,
    scopes: auth.user.scopes,
    children: /*#__PURE__*/_jsx(CreateActivityPermissionsContext.Provider, {
      value: permissions,
      children: /*#__PURE__*/_jsxs(OverrideCreateButtonContext.Provider, {
        value: OverrideCreateButtonComponent,
        children: [/*#__PURE__*/_jsx("div", {
          "data-onboarding": "create-activity-buttons",
          ref: sizeRef,
          children: /*#__PURE__*/_jsxs(Flex, {
            role: "list",
            justifyContent: "center",
            flexDirection: "row",
            flexWrap: "nowrap",
            paddingBlockStart: paddingBlockStart || '200',
            children: [renderedSelectedButtons && renderedSelectedButtons.map((button, index) => /*#__PURE__*/_jsx(View, {
              margin: "0",
              position: "relative",
              children: button
            }, `selected-button-${index}`)), renderOverflowActivityButton && /*#__PURE__*/_jsx(View, {
              margin: "0",
              position: "relative",
              children: renderOverflowActivityButton
            })]
          })
        }), buttonModal, RhumbMarkerSuccess]
      })
    })
  });
}
export default CreateActivityButtons;